import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@pisaka/ui';
import { Sparkles, PenLine, ALargeSmall, CircleHelp, ScrollText, Shield, RefreshCcw, Tag } from 'lucide-react';
import {
  basePageUrl, faqPageUrl, fontCreatorPageUrl, offerPageUrl, privacyPageUrl, refundPageUrl, termsPageUrl, aiPageUrl, oldBasePageUrl, pricingPageUrl
} from '../../../helpers/urls';

type PropsType = {
  isOpen: boolean;
  onClose: () => void;
  AuthContent?: ReactNode;
  StatsContent?: ReactNode;
};

const NavItem: FC<{ href: string; icon: React.ReactNode; label: string; onClick: () => void; bold?: boolean; active?: boolean; badge?: string }> = ({ href, icon, label, onClick, bold, active, badge }) => (
  <a
    href={href}
    onClick={onClick}
    className={`flex items-center gap-3 py-3 px-4 border-b border-gray-100 text-sm transition-colors ${bold ? 'font-medium' : ''} ${active ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'}`}
  >
    <span className={`shrink-0 ${active ? 'text-gray-700' : 'text-gray-400'}`}>{icon}</span>
    {label}
    {badge && (
      <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white uppercase">
        {badge}
      </span>
    )}
  </a>
);

export const SidebarComponent: FC<PropsType> = ({ isOpen, onClose, AuthContent, StatsContent }) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const isEn = i18n.language === 'en';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="left" className="w-[280px] flex flex-col justify-between p-0">
        <div>
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="text-left">
              {AuthContent}
            </SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col">
            <NavItem href={basePageUrl} icon={<PenLine className="w-4 h-4" />} label={t('sidebar.noteGenerator')} onClick={onClose} active={pathname === '/'} />
            <NavItem href={fontCreatorPageUrl} icon={<ALargeSmall className="w-4 h-4" />} label={t('sidebar.fontGenerator')} onClick={onClose} active={pathname.startsWith('/font-creator')} />
            <NavItem href={aiPageUrl} icon={<Sparkles className="w-4 h-4" />} label={t('sidebar.aiChat')} onClick={onClose} bold active={pathname.startsWith('/ai')} badge="new" />
            <NavItem href={pricingPageUrl} icon={<Tag className="w-4 h-4" />} label={t('sidebar.pricing')} onClick={onClose} active={pathname.startsWith('/pricing')} />
            <NavItem href={faqPageUrl} icon={<CircleHelp className="w-4 h-4" />} label={t('sidebar.faq')} onClick={onClose} active={pathname.startsWith('/faq')} />
            {!isEn && <NavItem href={offerPageUrl} icon={<ScrollText className="w-4 h-4" />} label={t('sidebar.offer')} onClick={onClose} active={pathname.startsWith('/offer')} />}
            {isEn && <NavItem href={termsPageUrl} icon={<ScrollText className="w-4 h-4" />} label={t('sidebar.terms')} onClick={onClose} active={pathname.startsWith('/terms')} />}
            <NavItem href={privacyPageUrl} icon={<Shield className="w-4 h-4" />} label={t('sidebar.privacy')} onClick={onClose} active={pathname.startsWith('/privacy')} />
            <NavItem href={refundPageUrl} icon={<RefreshCcw className="w-4 h-4" />} label={t('sidebar.refund')} onClick={onClose} active={pathname.startsWith('/refund')} />
          </nav>
        </div>
        <div className="p-4">
          {StatsContent}
          <p className="text-sm text-gray-500 mb-2">{t('sidebar.socials')}</p>
          <div className="flex gap-3">
            <a href="https://vk.com/pisaka.online" target="_blank" rel="noreferrer" title="VK" className="text-gray-500 hover:text-blue-600 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M15.07 2H8.93C3.33 2 2 3.33 2 8.93v6.14C2 20.67 3.33 22 8.93 22h6.14C20.67 22 22 20.67 22 15.07V8.93C22 3.33 20.67 2 15.07 2zm3.08 13.96h-1.6c-.6 0-.79-.48-1.87-1.57-1-.93-1.43-1.06-1.67-1.06-.34 0-.44.1-.44.58v1.43c0 .41-.13.66-1.22.66-1.8 0-3.8-1.09-5.2-3.13C5.1 10.48 4.7 8.8 4.7 8.43c0-.24.1-.47.58-.47h1.6c.43 0 .6.2.76.66.84 2.43 2.24 4.56 2.82 4.56.22 0 .32-.1.32-.65V10.1c-.07-1.17-.68-1.27-.68-1.69 0-.2.17-.4.44-.4h2.52c.37 0 .5.2.5.63v3.4c0 .37.17.5.27.5.22 0 .4-.13.8-.54 1.23-1.38 2.1-3.5 2.1-3.5.12-.24.3-.46.73-.46h1.6c.48 0 .59.25.48.63-.2.94-2.15 3.68-2.15 3.68-.17.28-.24.4 0 .71.17.24.73.73 1.1 1.17.68.78 1.2 1.43 1.34 1.88.14.44-.08.67-.53.67z"/></svg>
            </a>
            <a href="https://t.me/pisaka_online" target="_blank" rel="noreferrer" title="Telegram" className="text-gray-500 hover:text-blue-500 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8-1.68 7.92c-.12.56-.46.7-.93.43l-2.57-1.9-1.24 1.2c-.14.13-.25.25-.51.25l.18-2.6 4.7-4.25c.2-.18-.05-.28-.32-.1L7.9 14.67l-2.52-.79c-.55-.17-.56-.55.12-.81l9.85-3.8c.46-.17.86.11.29.53z"/></svg>
            </a>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100">
            <a href={oldBasePageUrl} onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
              {t('sidebar.oldNoteGenerator')}
            </a>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
